import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, Layers, Shield, Scan } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { useState } from 'react';
import { cn } from '@/lib/utils';

export const PlatformEngineeringHero = () => {
  const { session: _session } = useAuth();
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [isSimulating, setIsSimulating] = useState(false);
  // Twin teaser state (for Living Twin Layers + Current State Scanner as instantly functional visualization of impact modelling, per task E + DESIGN.md v1.1 reframing).
  // 5 toggleable layers from Canvas TWIN_LAYERS (see src/components/Canvas/hooks/useLayerOrganization.ts), scanner phrases + Forge mismatch from CanvasToolbar + ValidationService.
  // Filters demo nodes visually; Impact Mode activates red/orange dashed ripple; Scan State toggles inline report. Links use ?mode=impact.
  // No new files; uses cn(), var(--accent) teal, dark cards, same badge/node styles. Comments updated for reframing + Canvas impl.
  const [activeTwinLayer, setActiveTwinLayer] = useState<string | null>(null);
  const [impactMode, setImpactMode] = useState(false);
  const [scanReport, setScanReport] = useState<string | null>(null);

  // Updated per reframing: surface the twin value (5 toggleable layers + Current State Scanner as "instantly functional visualization" of impact modelling, not words alone)
  // in the external curiosity engine / hero / marketing front door. Visitors "get the idea" from seeing layers + live-like scanner teaser + Forge mismatch + ripple.
  // Implementation detail: full living twin + toggle + scanner + Impact Mode + ripple in Canvas (useLayerOrganization, CanvasToolbar, ResizableNode).
  // See DESIGN.md (Nordic calm, Canvas as primary RISK differentiator, hero teases matrix functionally).
  // Pure design vision teaser (see DESIGN_VISION.md + DESIGN.md v1.1).
  // Deliberately abstract / object-independent so the concept (the living matrix of
  // risk × impact × cost × cyber × versioning) is not framed by current recovery
  // examples or specific tools. Concrete instances populate the matrix; they do not
  // define the visual language or interaction model.
  //
  // Longer-term direction (per extended DESIGN_VISION): evolve toward a full
  // "Product Landscape Diagram" / component landscape view for solution clusters —
  // 2D surfaces (CI/CD, Security, Obs, Infra, Data...), free-form spanning tool
  // regions that can occupy multiple places, and import that visually identifies
  // holes/gaps in the organizational architecture of platform engineering surfaces.
  // The current 4-unit teaser is a minimal embodiment of the matrix; the landscape
  // map is the richer view that makes spanning comprehensive solutions and gap
  // detection tangible. See the landscape-map-vision.html artifact for the concept.
  //
  // Twin extension (this edit): integrated "Living Twin Layers" teaser below 4-unit demo using exact TWIN_LAYERS labels + scanner + Forge node + ripple.
  const demoNodes = [
    {
      id: 'entry',
      label: 'Entry / Boundary Unit',
      risk: 'High',
      score: 82,
      impact: 'change-high',
      cost: 'med-high',
      cyber: 'visible',
      ver: 'tracked',
      twinLayer: 'infrastructure',
    },
    {
      id: 'compute',
      label: 'Compute / Execution Layer',
      risk: 'Med',
      score: 61,
      impact: 'high',
      cost: 'med',
      cyber: 'layer',
      ver: 'stable',
      twinLayer: 'services',
    },
    {
      id: 'persist',
      label: 'Persistence / Data Layer',
      risk: 'Low',
      score: 31,
      impact: 'low',
      cost: 'low',
      cyber: 'awareness',
      ver: 'governed',
      twinLayer: 'dataflows',
    },
    {
      id: 'orchestrate',
      label: 'Orchestration / Flow Unit',
      risk: 'Med',
      score: 54,
      impact: 'med',
      cost: 'med-high',
      cyber: 'supply',
      ver: 'diffable',
      twinLayer: 'performance',
    },
  ];

  const getRiskClasses = (risk: string) => {
    if (risk === 'High')
      return 'bg-[var(--destructive)]/15 text-[var(--destructive)] border-[var(--destructive)]/40';
    if (risk === 'Med')
      return 'bg-[var(--warning)]/15 text-[var(--warning)] border-[var(--warning)]/40';
    return 'bg-[var(--success)]/15 text-[var(--success)] border-[var(--success)]/40';
  };

  // Exact 5 from TWIN_LAYERS (src/components/Canvas/hooks/useLayerOrganization.ts) for pills in teaser.
  const twinLayers = [
    { id: 'infrastructure', label: 'Infrastructure layer' },
    { id: 'services', label: 'Service / Application layer' },
    { id: 'dataflows', label: 'Data flow layer' },
    { id: 'policy', label: 'Policy / Compliance layer' },
    { id: 'performance', label: 'Performance / Impact layer' },
  ] as const;

  // Hardcoded scanner report using *exact* phrases from CanvasToolbar.tsx + ValidationService mismatch for vCluster + "Forge: 68%" style.
  const scannerReportText = `Current State Scanner — 4 nodes, 3 edges
Covered layers/planes: infrastructure, services, dataflows
Maturity proxy: 2
Policy active: no

Forge match for vCluster (multi-tenant):
  Score: 68
  Mismatches:
    - This vCluster template requires Level 3 multi-tenancy — your current state is Level 2
  Mismatches detected — agents can suggest adapted variant or alternative from Forge.

Tip: Scanner auto-switched to Impact Mode. Run Twin Scenario on a node to see ripples + DORA deltas on the current state.`;

  return (
    <section
      className="relative overflow-hidden bg-[var(--background)] pt-16 pb-20"
      aria-label="Platform Engineering Nexus — functional visualization of the complexity matrix"
    >
      {/* Subtle dark Nordic texture per DESIGN.md — restrained, no legacy gradients */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_rgba(255,255,255,0.025)_0%,_transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,_rgba(255,255,255,0.015)_0%,_transparent_55%)]" />

      <div className="relative container mx-auto px-4">
        {/* Minimal badge — recovery context only */}
        <div className="flex justify-center mb-6">
          <Badge
            variant="outline"
            className="inline-flex items-center gap-2 px-3 py-1 border-[var(--primary)]/30 text-[var(--primary)] text-xs tracking-[1px]"
          >
            RECOVERY MISSION • CALM CONTROL CENTER
          </Badge>
        </div>

        {/* Ultra-minimal headline. Value is the visualization, not copy. */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-normal sm:font-light tracking-[-0.02em] text-[var(--foreground)] leading-none mb-4">
            The matrix that makes
            <br />
            platform complexity visible.
          </h1>
          <p className="text-[var(--muted-foreground)] text-lg max-w-xl mx-auto">
            Risk × Impact × Cost × Cyber × Versioning — live, interactive, exportable.
          </p>
        </div>

        {/* Primary action right up front — "instantly functional" per user feedback */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
          <Button size="xl" variant="primary" asChild className="gap-2 text-base">
            <Link
              to="/canvas?template=basic-aws-idp"
              aria-label="Load basic-aws-idp template into the Canvas to see the live risk matrix"
            >
              <Layers className="h-5 w-5" />
              Load Template in Canvas
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
          <Button size="xl" variant="outline" asChild className="gap-2 text-base">
            <Link
              to="/idp-tools-catalog"
              aria-label="Browse IDP Tools Catalog with live cyber and OSINT risk enrichment"
            >
              <Shield className="h-5 w-5" />
              Explore live catalog
            </Link>
          </Button>
        </div>

        {/* THE STAR: High-fidelity, lightly interactive functional visualization of the exact matrix the user described.
            Spatially complex tooling with live workflow dependencies, actions, layers, dimensions and change management versioning — visibility of change impact and modeling change impact and cost. This is what the Canvas makes actionable.
            Calm Nordic tokens. No words needed to understand the value.
            This replaces generic marketing hero with the actual product promise made visible.
            Twin reframing (this edit): surfaces 5 toggleable TWIN_LAYERS + Current State Scanner teaser + Forge mismatch + ripple as "instantly functional visualization" (see Canvas for full impl). */}
        <div className="max-w-5xl mx-auto">
          <div
            className={cn(
              'relative rounded-2xl border border-[var(--border)] bg-[var(--card)] p-5 md:p-6 shadow-sm',
              impactMode && 'ring-1 ring-[var(--destructive)]/30'
            )}
            role="region"
            aria-label="Interactive demo of Canvas risk matrix + Living Twin Layers (5 toggleable) + scanner teaser for impact modelling"
            aria-describedby="matrix-caption"
          >
            {/* Header bar — shows the "load" state + living simulation control. Updated for twin: Impact Mode badge activates ripple + performance layer filter. */}
            <div className="flex items-center justify-between mb-4 text-sm">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[var(--muted-foreground)] text-xs tracking-widest">
                  ABSTRACT MATRIX
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-[var(--primary)]/10 text-[var(--primary)]">
                  LIVING TWIN TEASER • 5 LAYERS + SCANNER
                </span>
                {/* Impact Mode badge (per spec): clicking activates red/orange ripple line on the demo + filters to performance layer */}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    const next = !impactMode;
                    setImpactMode(next);
                    if (next) {
                      setActiveTwinLayer('performance');
                    } else if (activeTwinLayer === 'performance') {
                      setActiveTwinLayer(null);
                    }
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const next = !impactMode;
                      setImpactMode(next);
                      if (next) setActiveTwinLayer('performance');
                      else if (activeTwinLayer === 'performance') setActiveTwinLayer(null);
                    }
                  }}
                  className={cn(
                    'text-[10px] px-2 py-0.5 rounded border cursor-pointer select-none transition-colors focus:outline-none focus:ring-1 focus:ring-[var(--accent)]/50',
                    impactMode
                      ? 'bg-[var(--destructive)]/15 text-[var(--destructive)] border-[var(--destructive)]/40'
                      : 'bg-[var(--primary)]/10 text-[var(--primary)] border-[var(--primary)]/30 hover:bg-[var(--primary)]/15'
                  )}
                  aria-pressed={impactMode}
                >
                  Impact Mode
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[10px] text-[var(--primary)] hover:bg-[var(--primary)]/10"
                  onClick={() => {
                    setIsSimulating(true);
                    setSelectedNode('api'); // highlight the high cyber node as "scan" result
                    // purposeful motion: settle after short time (respects reduced-motion via duration)
                    setTimeout(() => {
                      setIsSimulating(false);
                    }, 1400);
                  }}
                  disabled={isSimulating}
                >
                  {isSimulating ? 'Scanning...' : 'Simulate load + risk scan'}
                </Button>
              </div>
              <div className="text-[10px] text-[var(--muted-foreground)] flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--destructive)]" /> 1
                high cyber
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--warning)]" /> 2
                change impact
              </div>
            </div>

            {/* The matrix visualization — flow of connected nodes. Hover or click a node to "model the impact".
                Layer pills (below) filter these nodes visually (dim non-matching). Per twin teaser spec. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 relative">
              {demoNodes.map(node => {
                const isSelected = selectedNode === node.id;
                // node.twinLayer is present on all demoNodes (inferred from literal init); no `any` to satisfy strict TS/ESLint.
                const nodeTwinLayer = node.twinLayer;
                const matchesFilter = !activeTwinLayer || nodeTwinLayer === activeTwinLayer;
                return (
                  <motion.div
                    key={node.id}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => setSelectedNode(isSelected ? null : node.id)}
                    className={cn(
                      'group rounded-xl border p-3 cursor-pointer transition-all bg-[var(--background)]',
                      isSelected
                        ? 'border-[var(--accent)] ring-1 ring-[var(--accent)]/30'
                        : 'border-[var(--border)] hover:border-[var(--accent)]/50',
                      !matchesFilter && 'opacity-40'
                    )}
                    aria-label={`${node.label} node: risk ${node.risk}(${node.score}), impact ${node.impact}, cost ${node.cost}${activeTwinLayer ? `, layer ${nodeTwinLayer}` : ''}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-medium text-[var(--foreground)] text-sm tracking-tight">
                        {node.label}
                      </div>
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-px rounded border ${getRiskClasses(node.risk)}`}
                      >
                        {node.risk}({node.score})
                      </span>
                    </div>

                    <div className="space-y-1 text-[10px] leading-snug text-[var(--muted-foreground)]">
                      <div>
                        impact: <span className="text-[var(--foreground)]/90">{node.impact}</span>
                      </div>
                      <div>
                        cost: <span className="text-[var(--foreground)]/90">{node.cost}</span>
                      </div>
                      <div>
                        cyber: <span className="text-[var(--foreground)]/90">{node.cyber}</span> •
                        ver: <span className="text-[var(--foreground)]/90">{node.ver}</span>
                      </div>
                    </div>

                    {/* Subtle "ripple" hint when selected — models change impact visually */}
                    {isSelected && (
                      <div className="mt-2 pt-2 border-t border-[var(--border)] text-[9px] text-[var(--accent)]">
                        Selecting this would cascade to 2 downstream • cost delta +18%
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Connection hint lines (evokes the graph edges without full xyflow cost on landing) */}
            <div className="hidden lg:block absolute left-[23%] right-[23%] top-[38%] h-px bg-gradient-to-r from-transparent via-[var(--border)]/60 to-transparent pointer-events-none" />
            <div className="hidden lg:block absolute left-[48%] right-[23%] top-[62%] h-px bg-gradient-to-r from-transparent via-[var(--border)]/40 to-transparent pointer-events-none" />

            {/* Dashed ripple line (red/orange) activated by "Impact Mode" badge or Scan State. Uses border-dashed + semantic for calm Nordic; models twin impact/ripple visually on the demo (not words). */}
            {impactMode && (
              <div className="hidden lg:block absolute left-[15%] right-[15%] top-[50%] border-t-[1.5px] border-dashed border-[var(--destructive)]/70 pointer-events-none" />
            )}

            {/* Living Twin Layers teaser — integrated below 4-unit demo matrix per spec.
                5 small pills/tabs using *exact* labels from TWIN_LAYERS.
                Mini "node" card: "Forge: 68%" badge + "mismatch: This vCluster template requires Level 3 multi-tenancy — your current state is Level 2"
                Simulated "Scan State" button toggles inline report div with scanner output using exact phrases (Current State Scanner, Forge match, Tip: ... Impact Mode, ripples...).
                Clicking layer pill filters demo nodes visually (opacity).
                "Impact Mode" (in header) activates the ripple.
                "instantly functional visualization" of impact modelling, not paragraphs. Links to /canvas?mode=impact .
                Calm Nordic: var(--accent) teal, dark cards, cn(), motion if present. Ref: Canvas impl. */}
            <div className="mt-3 pt-3 border-t border-[var(--border)]">
              <div className="flex items-center gap-2 mb-1.5 text-[10px]">
                <span className="font-mono text-[var(--muted-foreground)] tracking-widest">
                  LIVING TWIN LAYERS
                </span>
                <span className="text-[9px] px-1.5 py-px rounded bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/30">
                  5 PLANES • CLICK TO FILTER DEMO
                </span>
              </div>

              {/* 5 pills/tabs — exact labels */}
              <div className="flex flex-wrap gap-1 mb-2">
                {twinLayers.map(layer => {
                  const isActive = activeTwinLayer === layer.id;
                  return (
                    <button
                      key={layer.id}
                      type="button"
                      onClick={() => setActiveTwinLayer(isActive ? null : layer.id)}
                      className={cn(
                        'text-[9px] px-2 py-0.5 rounded-full border transition-colors focus:outline-none focus:ring-1 focus:ring-[var(--accent)]/40',
                        isActive
                          ? 'border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--accent)]'
                          : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--accent)]/60 hover:text-[var(--foreground)]'
                      )}
                      aria-pressed={isActive}
                    >
                      {layer.label}
                    </button>
                  );
                })}
              </div>

              {/* Mini "node" card showing Forge: 68% badge + exact mismatch sentence */}
              <div
                className={cn(
                  'mb-2 rounded-lg border p-2 text-[10px] bg-[var(--background)]',
                  impactMode && 'border-[var(--destructive)]/40 ring-1 ring-[var(--destructive)]/20'
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[var(--foreground)] tracking-tight">
                    vCluster (multi-tenant) template
                  </span>
                  <span className="text-[10px] font-semibold px-1.5 py-px rounded border bg-[var(--warning)]/15 text-[var(--warning)] border-[var(--warning)]/40">
                    Forge: 68%
                  </span>
                </div>
                <div className="mt-1 text-[var(--destructive)]/80 leading-snug">
                  mismatch: This vCluster template requires Level 3 multi-tenancy — your current
                  state is Level 2
                </div>
              </div>

              {/* Simulated Scan State button + toggle inline report using exact scanner phrases; also activates Impact + performance filter */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 px-2 text-[10px] text-[var(--primary)] hover:bg-[var(--primary)]/10"
                  onClick={() => {
                    if (scanReport) {
                      setScanReport(null);
                    } else {
                      setScanReport(scannerReportText);
                      setImpactMode(true);
                      setActiveTwinLayer('performance');
                    }
                  }}
                >
                  <Scan className="h-3 w-3 mr-1" /> Scan State
                </Button>
                {scanReport && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1 text-[9px] text-[var(--muted-foreground)]"
                    onClick={() => setScanReport(null)}
                    title="Clear scanner report"
                  >
                    ×
                  </Button>
                )}
                <Link
                  to="/canvas?mode=impact"
                  className="ml-auto text-[9px] text-[var(--accent)] hover:underline"
                  aria-label="Open full Canvas in Impact Mode with live twin layers and Current State Scanner"
                >
                  Try scanner in Canvas →
                </Link>
              </div>

              {scanReport && (
                <div className="mt-1 w-full text-[9px] bg-[var(--card)] border border-[var(--border)] rounded p-1.5 font-mono whitespace-pre-wrap text-[var(--muted-foreground)] max-h-28 overflow-auto">
                  {scanReport}
                </div>
              )}
            </div>

            {/* Export payoff — the "artifact" moment that makes it inspirational. Updated link for twin/impact. */}
            <div className="mt-4 pt-4 border-t border-[var(--border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
              <div className="text-[var(--muted-foreground)]">
                <span className="text-[var(--primary)] font-medium">Export produces:</span> IaC
                script + annotated diagram + one-page risk report with full change impact + cost
                modeling + versioning trace.
              </div>
              <Button size="sm" variant="default" asChild className="shrink-0">
                <Link to="/canvas?mode=impact&amp;template=basic-aws-idp">
                  Open full matrix in Canvas
                </Link>
              </Button>
            </div>
          </div>

          <p
            id="matrix-caption"
            className="mt-2 text-center text-[10px] text-[var(--muted-foreground)]/70 max-w-2xl mx-auto"
          >
            Spatially complex tooling with live workflow dependencies, actions, layers, dimensions
            and change management versioning — visibility of change impact and modeling change
            impact and cost. This is what the Canvas makes actionable.{' '}
            <Link to="/platform-landscape" className="text-[var(--accent)] hover:underline">
              See the full spatial Product Landscape Diagram (solutions positioned
              inside/outside/spanning the 5 planes from platformengineering.org/platform-tooling;
              import reveals holes in the component landscape).
            </Link>{' '}
            <span className="text-[var(--accent)]/70">
              (Hero now surfaces twin layers + scanner as functional differentiator.)
            </span>
          </p>
        </div>
      </div>
    </section>
  );
};
